import { useQuery } from '@tanstack/react-query';
import { Input } from 'antd';
import type { InputRef } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CandidateDto, CandidateListDto, FillLineDto } from '@pams/shared';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { FILL_COPY } from './copy';
import { OfficerResultRow } from './OfficerResultRow';
import { useDutyCounts } from './useDutyCounts';
import { useDutyMonths } from './useDutyMonths';
import { RECENT_PAS_CARD_ID, RecentPasCard } from './RecentPasCard';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE SEARCH SHEET — the whole acceptance criterion. [P4]
 *
 *  ⚠️ IT DOES NOT CLOSE BETWEEN SELECTIONS, AND THAT SINGLE BEHAVIOUR IS THE DIFFERENCE
 *  BETWEEN TWELVE CLICKS AND NONE. Selecting fires the write, clears the query and re-points
 *  the CURSOR at the next empty slot; the sheet stays open with the caret still in the box.
 *  Twelve names is type-enter twelve times, with no mouse between the first `+` and the last.
 *
 *  ⚠️ FOUR THINGS ARE LOAD-BEARING AND EACH WOULD SILENTLY COST CLICKS:
 *
 *  1. `onOpenAutoFocus` is OVERRIDDEN. `ui/sheet.tsx` renders its close button AFTER
 *     `{children}`, and Radix's default focuses the first focusable node it finds — which
 *     can be that button. The keyboard has to be in the search box on open.
 *  2. Enter calls the SAME `select()` the pointer path calls, never `activeOption.click()`.
 *     A synthetic click is a real pointer event: twelve of them, and the budget test's
 *     capture-phase counter reads twelve instead of zero.
 *  3. There is no `<form>` and this is a plain `Input`, never `Input.Search`. Enter inside a
 *     form triggers implicit submission, which fires a click on any submit button in scope.
 *  4. `onMouseDown` on a row calls `preventDefault()` before selecting, so the pointer path
 *     never blurs the input the keyboard path depends on.
 *
 *  ⚠️ AND IT SAYS WHERE IT IS, IN THREE PLACES. Radix marks everything outside the dialog
 *  `aria-hidden`, so the chip row behind it is unreadable to a screen reader while it is
 *  open — the title, the description and a `role="status"` line are the only channel left.
 *  The status line is the one that gets RE-announced when the cursor advances.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** Long enough to stop a burst of requests, short enough that nobody waits for it. */
const SEARCH_DEBOUNCE_MS = 200;

export type OfficerSearchSheetProps = {
  open: boolean;
  dutyId: string;
  slotRef: string;
  line: FillLineDto | null;
  /** "slot 2 of 4", one-based for a human. */
  slotIndex: number;
  slotTotal: number;
  scopeLabel: string;
  mode: 'fill' | 'swap';
  /**
   * ⚠️ [PRE-P6 §2b] EVERY OFFICER THIS SCREEN KNOWS IS ON THIS DUTY, INCLUDING WRITES THAT
   * HAVE NOT ANSWERED. ORed with the server's own `alreadyOnThisDuty`, never instead of it:
   * the server stays the authority and this only ever greys MORE. Without it an officer
   * named a moment ago stays offerable until the candidate query refetches.
   */
  namedOfficerIds: ReadonlySet<string>;
  onSelect: (officer: CandidateDto) => void;
  onClose: () => void;
};

export const OfficerSearchSheet = ({
  open,
  dutyId,
  slotRef,
  line,
  slotIndex,
  slotTotal,
  scopeLabel,
  mode,
  namedOfficerIds,
  onSelect,
  onClose,
}: OfficerSearchSheetProps) => {
  const inputRef = useRef<InputRef>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  /*
   * ⚠️ THE QUERY CLEARS WHEN THE CURSOR MOVES, NOT WHEN THE WRITE RESOLVES.
   *
   * Waiting for the response would put a network round trip between two keystrokes twelve
   * times over, which IS the budget. The write is in flight behind the sheet.
   *
   * ⚠️ ADJUSTED DURING RENDER, NOT IN AN EFFECT — React's documented pattern, and the same
   * one `useServerTable` uses to follow the URL into its search box. An effect would render
   * once with the previous slot's text still in the field and then again empty, so the
   * operator would see the last name they typed flash back for a frame on every advance.
   */
  const [renderedSlotRef, setRenderedSlotRef] = useState(slotRef);
  if (slotRef !== renderedSlotRef) {
    setRenderedSlotRef(slotRef);
    setQuery('');
    setDebounced('');
    setActive(0);
  }

  const candidates = useQuery({
    queryKey: queryKeys.fill.candidates(dutyId, slotRef, debounced),
    queryFn: ({ signal }) =>
      api.get<CandidateListDto>(`/duties/${dutyId}/candidates`, {
        query: { slotRef, ...(debounced ? { search: debounced } : {}) },
        signal,
      }),
    enabled: open && slotRef !== '',
    // The same question asked twice while the sheet is open is answered from cache.
    staleTime: 30_000,
  });

  const items = useMemo(() => candidates.data?.items ?? [], [candidates.data]);
  const clamped = Math.min(active, Math.max(items.length - 1, 0));
  /* ⚠️ [C5 §11b] The POINTER's row and its rectangle — `null` when the pointer is elsewhere. */
  const [hovered, setHovered] = useState<{ officerId: string; anchor: DOMRect } | null>(null);

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [D4.5 §3c] AN OFFICER ALREADY NAMED IN THIS DUTY POINT CANNOT BE PICKED AGAIN.
   *
   *  ⚠️ THIS IS NOT CONFLICT DETECTION, AND THE DISTINCTION IS THE WHOLE POINT.
   *  FR-5.3.3 defers double-booking entirely — no block, no warning, no cross-arrangement
   *  query — and `api/tests/integration/conflictDeferred.test.ts` holds an EXACT EMPTY LIST
   *  of files allowed to mention that vocabulary. What this reads is a UNIQUENESS check over
   *  rows the sheet already holds: `alreadyOnThisDuty` is computed per DEPLOYMENT, and a
   *  deployment is one duty point on one night. It issues no query, reads no duty window and
   *  never looks at another arrangement.
   *
   *  So an officer named on duty point 1 is greyed in duty point 1's search and NOT in duty
   *  point 2's — a morning card and an evening card may legitimately use the same person.
   *
   *  ⚠️ THE CLIENT DECIDES WHAT TO OFFER; THE SERVER IS UNCHANGED. [FIX-1]'s precedent for a
   *  retired post type: "a payload hand-crafted against a retired id would still be
   *  accepted". Naming somebody twice on one night is a typing mistake, not a privilege.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  /*
   * ⚠️ [PRE-P6 §2b] TWO SOURCES, ONE ANSWER. The server's flag is computed per DEPLOYMENT
   * when the candidate list was fetched; `namedOfficerIds` is what this screen has learned
   * since, including writes still in flight. ORed, so a stale list can only ever be
   * corrected in the safe direction.
   *
   * ⚠️ AND IT IS THE SAME PREDICATE THE ROW RENDERS FROM. Two expressions of "is this
   * officer already on?" would eventually disagree, and the one that matters is the one the
   * operator cannot see.
   */
  const alreadyOn = (officer: CandidateDto): boolean => officer.alreadyOnThisDuty || namedOfficerIds.has(officer.id);

  const select = (officer: CandidateDto | undefined): void => {
    if (!officer || alreadyOn(officer)) return;
    onSelect(officer);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      // Clamped, never wrapping: a result list that loops makes people lose their place.
      setActive((current) => Math.min(current + 1, Math.max(items.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter') {
      /* ⚠️ [D4.5 §3c] `select` REFUSES an already-named officer, so Enter on a greyed row is
         a no-op rather than a duplicate. The cursor is NOT made to skip them: the arrow keys
         and the visible list must agree, or `aria-activedescendant` points somewhere the eye
         does not — and the row is greyed precisely so it can still be READ. */
      event.preventDefault();
      select(items[clamped]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  /*
   * ⚠️ [C3c §4b] THE COUNTS COME BEHIND THE NAMES, AND THEY ARE A SEPARATE REQUEST ENTIRELY.
   *
   * `items` is whatever the candidate query has answered; this asks about exactly those ids, five
   * at a time. When the search term changes the ids change, so every batch key changes with it and
   * an in-flight answer for the old term lands in a cache entry nothing renders — which is how
   * §4b's "the queue resets" is satisfied without any cancellation at all.
   */
  const dutyCounts = useDutyCounts({
    open,
    dutyId,
    slotRef,
    officerIds: items.map((officer) => officer.id),
  });

  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C5 §11b] WHICH ROW THE RECENT-PAs CARD DESCRIBES — THE POINTER, ELSE THE CURSOR.
   *
   *  Two inputs, one card. The pointer wins while it is over a row (an operator moving a mouse
   *  is asking about the row under it, whatever the arrow keys last selected); otherwise the
   *  `aria-activedescendant` cursor answers, which is what "focus" means in this listbox and is
   *  the only way the counts are reachable from a keyboard at all.
   *
   *  ⚠️ THE KEYBOARD ANCHOR IS READ FROM THE DOM BY ID rather than kept in state. There is no
   *  event to hang a rectangle off when a cursor MOVES — `clamped` is derived, not dispatched —
   *  and a row's rectangle changes as the list scrolls under it, so a stored one goes stale.
   *  `getBoundingClientRect` during render is what `OfficerHoverCard` already relies on, and
   *  jsdom reports all-zero for both, which is why the RECTANGLE is a browser assertion.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const cursorOfficerId = hovered?.officerId ?? items[clamped]?.id ?? null;
  const cursorAnchor =
    hovered?.anchor ??
    (cursorOfficerId === null
      ? null
      : (document.getElementById(`fill-option-${cursorOfficerId}`)?.getBoundingClientRect() ?? null));

  const dutyMonths = useDutyMonths({ dutyId, slotRef, officerId: open ? cursorOfficerId : null });

  const lineLabel = line?.label ?? candidates.data?.lineLabel ?? '';
  const rankNames = (line?.eligibleRanks ?? candidates.data?.eligibleRanks ?? []).map((rank) => rank.shortName);

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-full gap-0 p-0 sm:max-w-md"
        data-testid="candidate-sheet"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <div className="flex flex-col gap-1 border-b border-border px-4 py-3">
          <SheetTitle>{mode === 'swap' ? `Swap on ${lineLabel}` : lineLabel}</SheetTitle>
          <SheetDescription>{FILL_COPY.eligible(rankNames)}</SheetDescription>
          {/* ⚠️ THE ONLY THING RE-ANNOUNCED WHEN THE CURSOR MOVES. */}
          <span role="status" aria-live="polite" className="text-xs text-muted-foreground" data-testid="sheet-position">
            {FILL_COPY.position(lineLabel, slotIndex, slotTotal, scopeLabel)}
          </span>
        </div>

        <div className="px-4 py-3">
          <Input
            ref={inputRef}
            role="combobox"
            aria-expanded
            aria-controls="fill-results"
            aria-autocomplete="list"
            aria-activedescendant={items[clamped] ? `fill-option-${items[clamped].id}` : undefined}
            aria-label={FILL_COPY.searchLabel}
            placeholder={FILL_COPY.searchPlaceholder}
            value={query}
            allowClear={false}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            data-testid="candidate-search"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
          <p className="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {debounced ? FILL_COPY.matchesHeading : FILL_COPY.recentHeading}
          </p>
          <ul id="fill-results" role="listbox" aria-label={FILL_COPY.resultsLabel} className="flex flex-col">
            {items.map((officer, index) => (
              <li
                key={officer.id}
                id={`fill-option-${officer.id}`}
                role="option"
                aria-selected={index === clamped}
                /*
                 * ⚠️ [C5 §11b/§11d] THE POPOVER OPENS ON HOVER — AND ON THE ARROW-KEY CURSOR,
                 * which is the `index === clamped` branch below rather than a `focus` event.
                 *
                 * These rows never receive DOM focus: focus stays in the combobox and the cursor
                 * is `aria-activedescendant`, which is what keeps the twelve-name click budget.
                 * So [PRE-P6.1]'s "opens on FOCUS as well as hover" is satisfied by the cursor,
                 * and an `onFocus` here would fire for nobody.
                 *
                 * ⚠️ ONE REQUEST PER OFFICER, EVER. `useDutyMonths` is held ONCE by this sheet
                 * with `staleTime: Infinity`, so a second hover over the same row answers from
                 * the cache and fires nothing — §11d.
                 */
                onMouseEnter={(event) => {
                  setHovered({ officerId: officer.id, anchor: event.currentTarget.getBoundingClientRect() });
                }}
                onMouseLeave={() => {
                  setHovered(null);
                }}
                aria-describedby={cursorOfficerId === officer.id ? RECENT_PAS_CARD_ID : undefined}
                /* ⚠️ [D4.5 §3c] GREYED, NOT REMOVED — an operator who cannot find somebody
                 has to see WHY. `aria-disabled` rather than `disabled`: a `<li>` has no
                 disabled state, and the row must stay in the listbox so a screen reader
                 reaches it and reads the reason beside the name. */
                aria-disabled={alreadyOn(officer) || undefined}
                className={alreadyOn(officer) ? 'cursor-not-allowed' : 'cursor-pointer'}
                // ⚠️ `preventDefault` FIRST: a mousedown that blurs the input takes the
                // keyboard out of the box the next name is typed into.
                onMouseDown={(event) => {
                  event.preventDefault();
                  select(officer);
                }}
              >
                <OfficerResultRow
                  officer={officer}
                  active={index === clamped}
                  alreadyOn={alreadyOn(officer)}
                  dutyCount={
                    dutyCounts.counts.get(officer.id) ?? (dutyCounts.pending.has(officer.id) ? 'pending' : null)
                  }
                />
              </li>
            ))}
          </ul>
          {/*
            ⚠️ [C5 §11b] ONE CARD FOR THE SHEET, NEVER ONE PER ROW — divergence #82's measured
            cost, and a search sheet re-renders its rows on every keystroke. It renders only when
            there is a row to describe, so an idle sheet mounts no portal at all.
          */}
          {cursorAnchor === null || cursorOfficerId === null ? null : (
            <RecentPasCard state={dutyMonths} anchor={cursorAnchor} />
          )}
          {items.length === 0 && !candidates.isFetching && (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              {debounced ? FILL_COPY.noneMatching : FILL_COPY.noneEligible}
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
